import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, useUser } from '../../auth';
import type { Profile } from '@modelcast/types';
import { Wordmark } from '../../components/Wordmark';
import { getDisplayNameValidationMessage } from '../../shared/validation/displayName';
import { safeResumeTarget } from '../../auth/resume';
import { jsonAuthHeaders } from '../../shared/authHeaders';
import { useCurrentProfile } from '../../shared/CurrentProfileProvider';

function normalizeUsername(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 30);
}

function getErrorMessage(error: unknown) {
  if (error instanceof Error) return error.message;
  return 'Something went wrong';
}

export function CompleteProfilePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { isLoaded: authLoaded, isSignedIn, getToken } = useAuth();
  const { isLoaded: userLoaded, user } = useUser();
  const {
    profile: currentProfile,
    isLoading: isCurrentProfileLoading,
    status: currentProfileStatus,
    refresh,
  } = useCurrentProfile();
  const rawResumeTarget = searchParams.get('redirect_url');
  // Approved failure path: the user is signed in here, so an invalid resume
  // target falls back to the dashboard. No target at all keeps the
  // historical destination (the new profile page, below).
  const redirect = rawResumeTarget ? safeResumeTarget(rawResumeTarget, { fallback: '/dashboard' }) : null;
  const suggestedDisplayName = user?.fullName || user?.firstName || '';
  const suggestedUsername = useMemo(() => {
    const emailName = user?.primaryEmailAddress?.emailAddress.split('@')[0] || '';
    return normalizeUsername(user?.username || suggestedDisplayName || emailName);
  }, [suggestedDisplayName, user?.primaryEmailAddress?.emailAddress, user?.username]);

  const [displayName, setDisplayName] = useState('');
  const [username, setUsername] = useState('');
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [existingProfile, setExistingProfile] = useState<Profile | null>(null);
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userLoaded) return;
    setDisplayName((current) => current || suggestedDisplayName);
    setUsername((current) => current || suggestedUsername);
  }, [suggestedDisplayName, suggestedUsername, userLoaded]);

  useEffect(() => {
    if (!authLoaded || !isSignedIn) return;
    if (isCurrentProfileLoading) {
      setLoadingProfile(true);
      return;
    }

    if (currentProfileStatus === 'error') {
      setError('Failed to check your profile');
      setLoadingProfile(false);
      return;
    }

    if (currentProfile?.username) {
      navigate(redirect || `/u/${currentProfile.username}`, { replace: true });
      return;
    }

    setExistingProfile(currentProfile);
    if (currentProfile) {
      setDisplayName((current) => current || currentProfile.displayName);
    }
    setLoadingProfile(false);
  }, [
    authLoaded,
    currentProfile,
    currentProfileStatus,
    isCurrentProfileLoading,
    isSignedIn,
    navigate,
    redirect,
  ]);

  useEffect(() => {
    const normalized = normalizeUsername(username);
    setUsernameAvailable(null);

    if (normalized.length < 3) return;

    let cancelled = false;
    const timeout = window.setTimeout(async () => {
      setCheckingUsername(true);
      try {
        const response = await fetch(`/api/profiles/username-available/${encodeURIComponent(normalized)}`);
        const data = await response.json();
        if (!cancelled) {
          setUsernameAvailable(Boolean(data.available));
        }
      } catch {
        if (!cancelled) {
          setUsernameAvailable(null);
        }
      } finally {
        if (!cancelled) {
          setCheckingUsername(false);
        }
      }
    }, 300);

    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [username]);

  if (authLoaded && !isSignedIn) {
    const redirectUrl = encodeURIComponent(`${location.pathname}${location.search}`);
    return <Navigate to={`/sign-in?redirect_url=${redirectUrl}`} replace />;
  }

  const normalizedUsername = normalizeUsername(username);
  const usernameIsValid = /^[a-z0-9_]{3,30}$/.test(normalizedUsername);
  const profileUrlPrefix = `${window.location.host}/u/`;
  const displayNameError = getDisplayNameValidationMessage(displayName);
  const canSubmit =
    !displayNameError &&
    usernameIsValid &&
    usernameAvailable === true &&
    !saving &&
    !loadingProfile;

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;

    setSaving(true);
    setError(null);

    try {
      // The in-memory bearer token is gone after a full-page navigation (the
      // email verification link lands here in a fresh page). The httpOnly
      // session cookie still authenticates, so send the request either way.
      const token = await getToken();
      const response = await fetch(existingProfile ? '/api/profiles/me' : '/api/profiles', {
        method: existingProfile ? 'PATCH' : 'POST',
        headers: jsonAuthHeaders(token),
        body: JSON.stringify({
          displayName: displayName.trim(),
          username: normalizedUsername,
        }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to create your profile');
      }

      const data = await response.json();
      const profile = data.profile as Profile;
      await refresh();
      navigate(redirect || `/u/${profile.username || normalizedUsername}`, { replace: true });
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-bg">
      <header className="border-b border-line">
        <div className="page-container h-14 flex items-center">
          <Wordmark />
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-5 py-12">
        <form onSubmit={handleSubmit} className="w-full max-w-[440px] bg-panel border border-line rounded-xl shadow-lg p-8">
          <div className="mb-8">
            <p className="label-mono mb-3">Finish signup</p>
            <h1 className="font-display text-[40px] leading-none font-light mb-3">Create your profile URL</h1>
            <p className="text-sm text-text-dim">
              Choose a username for your public DrawClub profile.
            </p>
          </div>

          <div className="flex flex-col gap-5">
            <label className="flex flex-col gap-2">
              <span className="label-mono">Name</span>
              <input
                type="text"
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
                className="input text-[15px]"
                placeholder="Your name or stage name"
                disabled={loadingProfile || saving}
                required
              />
              <span className={`min-h-5 text-xs ${displayName.trim() && displayNameError ? 'text-red-700' : 'text-text-dim'}`}>
                {displayName.trim() && displayNameError ? displayNameError : 'This is shown publicly on credits and profiles.'}
              </span>
            </label>

            <label className="flex flex-col gap-2">
              <span className="label-mono">Username</span>
              <div className="flex h-[52px] items-center rounded-xl border border-line bg-bg-secondary focus-within:border-accent/60">
                <span className="pl-4 text-[15px] text-text-faint">{profileUrlPrefix}</span>
                <input
                  type="text"
                  value={username}
                  onChange={(event) => setUsername(normalizeUsername(event.target.value))}
                  className="min-w-0 flex-1 bg-transparent px-1 pr-4 text-[15px] text-text outline-none"
                  placeholder="username"
                  disabled={loadingProfile || saving}
                  required
                />
              </div>
              <span className="min-h-5 text-xs text-text-dim">
                {normalizedUsername.length > 0 && !usernameIsValid && 'Use 3-30 lowercase letters, numbers, or underscores.'}
                {usernameIsValid && checkingUsername && 'Checking availability...'}
                {usernameIsValid && !checkingUsername && usernameAvailable === true && 'Username is available.'}
                {usernameIsValid && !checkingUsername && usernameAvailable === false && 'That username is already taken.'}
              </span>
            </label>
          </div>

          {error && (
            <div className="mt-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <button type="submit" className="btn btn-accent btn-lg mt-7 w-full" disabled={!canSubmit}>
            {saving ? 'Creating profile...' : 'Create profile'}
          </button>

          <p className="mt-5 text-center text-sm text-text-dim">
            Already have an account?{' '}
            <Link to="/sign-in" className="text-text underline underline-offset-4">
              Sign in
            </Link>
          </p>
        </form>
      </main>
    </div>
  );
}
